import booleanEqual from "@turf/boolean-equal";
import { Feature, GeoJsonProperties, Geometry, Point, Position } from "geojson";
import { RBush } from "geojson-rbush";

import { feature as toFeature } from "fond/turf";
import { MuPowerEditableLayerIds, MuPowerInputLayerIds } from "fond/types";

const EPSILON = 0.0000001;

/**
 * Determines if a Feature<Point> is collocated with a feature in the specified source layer
 * within the "project-layers" vector tile source.
 */
export const isConnected = (point: Feature<Point>, map: mapboxgl.Map, sourceLayer: string): boolean =>
  map?.querySourceFeatures("project-layers", { sourceLayer }).some((f) => booleanEqual(f.geometry, point.geometry, { precision: 7 }));

/**
 * Determines if a Leadin Feature<LineString> is valid by checking that its vertices
 * are collocated with both a UG point & a demand/address point.
 */
export const isValid = (feature: GeoJSON.Feature<GeoJSON.LineString>, map: mapboxgl.Map): boolean => {
  const points = feature.geometry.coordinates.map((coord) => toFeature({ type: "Point", coordinates: coord }));
  const hasUGPoint = points.some((point) => isConnected(point, map, MuPowerEditableLayerIds.ugPoints));
  const hasDemand = points.some((point) => isConnected(point, map, MuPowerInputLayerIds.addresses));
  return hasUGPoint && hasDemand;
};

/**
 * Determines if a Leadin Feature<LineString> is valid by checking that its vertices
 * are collocated with both a UG point & a demand/address point, using a spatial index.
 */
export const isValidTree = (feature: GeoJSON.Feature<GeoJSON.LineString>, tree: RBush<Geometry, GeoJsonProperties>) => {
  const nearbyToPointOne = getNearbyFeatures(feature.geometry.coordinates[0], tree);
  const nearbyToPointTwo = getNearbyFeatures(feature.geometry.coordinates[1], tree);

  const connectedFeatures = [...nearbyToPointOne, ...nearbyToPointTwo];
  const hasDemand = connectedFeatures.some((f) => f.properties?.layerId === MuPowerInputLayerIds.addresses);
  const hasUgPoints = connectedFeatures.some((f) => f.properties?.layerId === MuPowerEditableLayerIds.ugPoints);

  return hasDemand && hasUgPoints;
};

const getNearbyFeatures = (coordinates: Position, tree: RBush<Geometry, GeoJsonProperties>) => {
  return tree.search([coordinates[0] - EPSILON, coordinates[1] - EPSILON, coordinates[0] + EPSILON, coordinates[1] + EPSILON]).features;
};
